import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { FileText, Download, Send, Check, X, MoreHorizontal, Trash2, XCircle, Filter, ClipboardList } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const quoteStatusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
  accepte_attente_bc: { label: 'En attente BC', color: 'bg-amber-100 text-amber-800' },
  accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-800' },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800' },
};

export function QuotesTab({
  quotes, filteredQuotes, paginatedQuotes, loading,
  quoteStatusFilter, setQuoteStatusFilter,
  quoteStartDate, setQuoteStartDate,
  quoteEndDate, setQuoteEndDate,
  quotePageSize, setQuotePageSize,
  quoteCurrentPage, setQuoteCurrentPage, quoteTotalPages,
  isUser, isAdmin, canManage,
  resetQuoteFilters,
  onDownloadPdf, onAccept, onStatusChange, onResetStatus,
  onOpenPoValidation, onOpenEditQuote, onSend,
  onCancel, onDelete,
}) {
  return (
    <>
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">Filtres :</span>
            </div>
            <div className="flex-1 min-w-[150px] max-w-[180px]">
              <Label className="text-xs text-slate-500 mb-1 block">Statut</Label>
              <Select value={quoteStatusFilter} onValueChange={setQuoteStatusFilter}>
                <SelectTrigger data-testid="quote-status-filter" className="h-9"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="envoye">Envoyé</SelectItem>
                  <SelectItem value="accepte_attente_bc">En attente BC</SelectItem>
                  <SelectItem value="accepte">Accepté</SelectItem>
                  <SelectItem value="refuse">Refusé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date début</Label>
              <Input type="date" value={quoteStartDate} onChange={(e) => setQuoteStartDate(e.target.value)} className="h-9" data-testid="quote-start-date-filter" />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date fin</Label>
              <Input type="date" value={quoteEndDate} onChange={(e) => setQuoteEndDate(e.target.value)} className="h-9" data-testid="quote-end-date-filter" />
            </div>
            {(quoteStatusFilter !== 'all' || quoteStartDate || quoteEndDate) && (
              <Button variant="ghost" size="sm" onClick={resetQuoteFilters} className="text-slate-500 hover:text-slate-700">
                <X className="h-4 w-4 mr-1" /> Effacer
              </Button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500">Lignes :</span>
              <Select value={String(quotePageSize)} onValueChange={(v) => setQuotePageSize(v === 'all' ? 'all' : Number(v))}>
                <SelectTrigger className="h-8 w-20" data-testid="quote-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="60">60</SelectItem>
                  <SelectItem value="120">120</SelectItem>
                  <SelectItem value="all">Tous</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">{filteredQuotes.length} / {quotes.length} résultat(s)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" style={{ width: 40, height: 40 }} /></div>
      ) : filteredQuotes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">{quotes.length === 0 ? "Aucun devis" : "Aucun résultat pour ces filtres"}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Devis</TableHead>
                <TableHead>Bénéficiaire</TableHead>
                <TableHead>Participation</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedQuotes.map((quote) => (
                <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                  <TableCell className="font-medium">{quote.quote_number}</TableCell>
                  <TableCell>{quote.client_name}</TableCell>
                  <TableCell>{quote.amount.toLocaleString('fr-FR')} €</TableCell>
                  <TableCell>{format(parseISO(quote.created_at), 'd MMM yyyy', { locale: fr })}</TableCell>
                  <TableCell>
                    <Badge className={quoteStatusConfig[quote.status]?.color}>{quoteStatusConfig[quote.status]?.label}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onDownloadPdf(quote)}><Download className="mr-2 h-4 w-4" /> Télécharger PDF</DropdownMenuItem>
                        {quote.status === 'envoye' && (
                          <>
                            <DropdownMenuItem onClick={() => onAccept(quote)}><Check className="mr-2 h-4 w-4 text-emerald-600" /> Accepter le devis</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onStatusChange(quote.id, 'refuse')}><X className="mr-2 h-4 w-4 text-red-600" /> Refuser le devis</DropdownMenuItem>
                          </>
                        )}
                        {!isUser && (
                          <>
                            {quote.status === 'accepte_attente_bc' && (
                              <DropdownMenuItem onClick={() => onOpenPoValidation(quote)}><ClipboardList className="mr-2 h-4 w-4 text-amber-600" /> Saisir le Bon de commande</DropdownMenuItem>
                            )}
                            {(quote.status === 'brouillon' || quote.status === 'envoye') && (
                              <>
                                <DropdownMenuItem onClick={() => onOpenEditQuote(quote)}><FileText className="mr-2 h-4 w-4" /> Modifier le devis</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onSend(quote.id)}><Send className="mr-2 h-4 w-4" /> {quote.status === 'brouillon' ? 'Envoyer au bénéficiaire' : 'Renvoyer au bénéficiaire'}</DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                        {isAdmin && (
                          <>
                            {(quote.status === 'accepte' || quote.status === 'refuse' || quote.status === 'accepte_attente_bc') && (
                              <>
                                <DropdownMenuItem onClick={() => onResetStatus(quote.id, 'envoye')}><Send className="mr-2 h-4 w-4 text-blue-600" /> Repasser en "Envoyé"</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onResetStatus(quote.id, 'brouillon')}><FileText className="mr-2 h-4 w-4 text-slate-600" /> Repasser en "Brouillon"</DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                        {canManage && (
                          <>
                            {quote.status !== 'refuse' && quote.status !== 'accepte' && quote.status !== 'accepte_attente_bc' && (
                              <DropdownMenuItem onClick={() => onCancel(quote)} className="text-amber-600"><XCircle className="mr-2 h-4 w-4" /> Annuler le devis</DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onDelete(quote)} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Supprimer</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {filteredQuotes.length > 0 && quotePageSize !== 'all' && (
        <div className="flex items-center justify-between mt-4 px-2">
          <p className="text-sm text-slate-500">Page {quoteCurrentPage} sur {quoteTotalPages} ({paginatedQuotes.length} affichés sur {filteredQuotes.length})</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setQuoteCurrentPage(1)} disabled={quoteCurrentPage === 1}>««</Button>
            <Button variant="outline" size="sm" onClick={() => setQuoteCurrentPage(p => Math.max(1, p - 1))} disabled={quoteCurrentPage === 1}>Précédent</Button>
            <Button variant="outline" size="sm" onClick={() => setQuoteCurrentPage(p => Math.min(quoteTotalPages, p + 1))} disabled={quoteCurrentPage === quoteTotalPages}>Suivant</Button>
            <Button variant="outline" size="sm" onClick={() => setQuoteCurrentPage(quoteTotalPages)} disabled={quoteCurrentPage === quoteTotalPages}>»»</Button>
          </div>
        </div>
      )}
    </>
  );
}
